// src/components/course/HTMLContent.jsx
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { BASELINE } from '../../../../util';
import { IoClose } from 'react-icons/io5';

// Fetch a signed video token via axios (sends session cookie), then return proxy URL
const getSignedVideoUrl = async (src) => {
  const match = src && src.match(/Class_Materials\/.+/);
  if (!match) return src;
  const p = btoa(match[0]);
  try {
    const { data } = await axios.get(`${BASELINE}api/video-token`, {
      params: { p },
      withCredentials: true,
    });
    return `${BASELINE}api/video?p=${encodeURIComponent(data.p)}&expiry=${data.expiry}&sig=${data.sig}`;
  } catch {
    return src; // fallback to original src if token fetch fails
  }
};

// Image Modal Component
const ImageModal = ({ src, alt, isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80"
      onClick={onClose}
    >
      <div className="relative max-w-[90%] max-h-[90vh] bg-white rounded-lg p-1 shadow-xl">
        <button 
          className="absolute top-2 right-2 text-white bg-black bg-opacity-70 rounded-full p-1 hover:bg-opacity-90 z-10"
          onClick={onClose}
        >
          <IoClose size={24} />
        </button>
        <img 
          src={src} 
          alt={alt || 'Image preview'} 
          className="max-w-full max-h-[90vh] object-contain rounded-lg" 
        />
      </div>
    </div>
  );
};

const HTMLContent = ({ htmlPath, setChatInput }) => {
  const [content, setContent] = useState('');
  const [error, setError] = useState(null);
  const contentRef = useRef(null);
  const [modalImage, setModalImage] = useState({ src: '', alt: '', isOpen: false });

  // Load HTML content and head links
  useEffect(() => {
    const fetchHTML = async () => {
      try {
        const response = await axios.get(BASELINE + htmlPath, { responseType: 'text' });
        const text = response.data;

        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');

        // If <head> exists in the fetched document, load its link tags (e.g., fonts and styles)
        const headLinks = doc.head.querySelectorAll('link[rel="stylesheet"]');
        headLinks.forEach(link => {
          const href = link.getAttribute('href');
          // Check if the link is already present in document.head
          if (!document.querySelector(`link[href="${href}"]`)) {
            const newLink = document.createElement('link');
            newLink.rel = 'stylesheet';
            newLink.href = href;
            // Copy integrity and crossorigin if available
            if (link.getAttribute('integrity')) {
              newLink.integrity = link.getAttribute('integrity');
            }
            if (link.getAttribute('crossorigin')) {
              newLink.crossOrigin = link.getAttribute('crossorigin');
            }
            document.head.appendChild(newLink);
          }
        });

        // Extract body content if available, otherwise use full text
        let bodyContent = text;
        if (text.includes('<body>') && text.includes('</body>')) {
          const bodyMatch = text.match(/<body[^>]*>([\s\S]*)<\/body>/i);
          if (bodyMatch && bodyMatch[1]) {
            bodyContent = bodyMatch[1];
          }
        }

        // Strip video <source src="..."> BEFORE rendering — prevents browser
        // from making a request to the real URL. Store as data-src instead.
        bodyContent = bodyContent.replace(
          /<source([^>]*)\ssrc=(["'])([^"']+)\2([^>]*)>/gi,
          (_, before, quote, src, after) =>
            `<source${before} data-src=${quote}${src}${quote}${after}>`
        );

        setContent(bodyContent);
      } catch (err) {
        console.error('Error fetching HTML:', err);
        setError(err.message);
      }
    };

    if (htmlPath) {
      fetchHTML();
    }
  }, [htmlPath]);

  // Protect video sources — remove src from DOM, set via JS only
  useEffect(() => {
    if (!contentRef.current || !content) return;
    const videos = Array.from(contentRef.current.querySelectorAll('video'));
    const blobUrls = [];

    videos.forEach(video => {
      // Enforce restrictions
      video.setAttribute('controlsList', 'nodownload noremoteplayback nofullscreen');
      video.setAttribute('disablePictureInPicture', '');
      video.removeAttribute('oncontextmenu');

      // Block all right-click / context menu
      video.addEventListener('contextmenu', e => e.preventDefault());

      // Block keyboard shortcuts that might trigger download (e.g. Ctrl+S while focused)
      video.addEventListener('keydown', e => {
        if ((e.ctrlKey || e.metaKey) && ['s','u'].includes(e.key.toLowerCase())) {
          e.preventDefault();
        }
      });

      // Read from data-src (src was stripped before render to block early browser fetch)
      const source = video.querySelector('source');
      const rawSrc = (source && (source.getAttribute('data-src') || source.getAttribute('src'))) || '';
      if (rawSrc) {
        if (source) {
          source.removeAttribute('data-src');
          source.removeAttribute('src');
        }
        getSignedVideoUrl(rawSrc).then(signedUrl => {
          video.src = signedUrl;
          video.load();
        });
      }
    });

    return () => {
      blobUrls.forEach(u => URL.revokeObjectURL(u));
    };
  }, [content]);

  // Setup image click handlers
  useEffect(() => {
    if (contentRef.current) {
      const images = contentRef.current.querySelectorAll('img');
      
      images.forEach(img => {
        // Add a cursor pointer style to indicate it's clickable
        img.style.cursor = 'pointer';
        
        // Add click event listener
        img.addEventListener('click', (e) => {
          setModalImage({
            src: e.target.src,
            alt: e.target.alt || 'Image preview',
            isOpen: true
          });
        });
      });
      
      // Cleanup event listeners
      return () => {
        if (contentRef.current) {
          const images = contentRef.current.querySelectorAll('img');
          images.forEach(img => {
            img.removeEventListener('click', () => {});
          });
        }
      };
    }
  }, [content]);

  // Transform A/B/C/D choices into interactive buttons
  useEffect(() => {
    if (!contentRef.current || !content) return;
    const timer = setTimeout(() => {
      const el = contentRef.current;

      // ── Correct-answer detector ───────────────────────
      const findCorrect = (container) => {
        if (!container) return null;
        // Collect all solution-like elements
        const solEls = [
          container.querySelector('.correct-answer'),
          container.querySelector('.question-solution'),
          ...Array.from(container.querySelectorAll('[id^="question-solution-"]')),
        ].filter(Boolean);

        for (const solEl of solEls) {
          // Check innerHTML for bold/strong letter: <b>C</b>
          const html = solEl.innerHTML || '';
          const boldH = html.match(/answer is\s+<b>([A-D])<\/b>/i)
                     || html.match(/answer is\s+<strong>([A-D])<\/strong>/i)
                     || html.match(/\banswer\b[^<]*<b>([A-D])<\/b>/i);
          if (boldH) return boldH[1].toUpperCase();

          // Check textContent
          const txt = solEl.textContent || '';
          const m = txt.match(/answer is\s+([A-D])\b/i)
                 || txt.match(/correct answer is\s*([A-D])/i)
                 || txt.match(/\banswer\s*:\s*([A-D])\b/i)
                 || txt.match(/\(([A-D])\)\s*is correct/i);
          if (m) return m[1].toUpperCase();
        }
        return null;
      };

      // ── Wire up click handlers ────────────────────────
      const attachChoiceHandlers = (container, choiceEls, correctLetter, qContainer) => {
        if (container.hasAttribute('data-initialized')) return;
        container.setAttribute('data-initialized', '1');
        choiceEls.forEach(choice => {
          const letterEl = choice.querySelector('.choice-letter');
          const letter = (letterEl?.textContent || choice.getAttribute('data-letter') || '').trim().toUpperCase();
          if (!letter) return;
          choice.addEventListener('click', () => {
            if (container.classList.contains('answered')) return;
            container.classList.add('answered');
            choice.classList.add('selected');
            const isCorrect = correctLetter && letter === correctLetter;
            choice.classList.add(isCorrect ? 'correct' : 'incorrect');
            if (!isCorrect && correctLetter) {
              const cEl = Array.from(choiceEls).find(c =>
                (c.querySelector('.choice-letter')?.textContent || c.getAttribute('data-letter') || '').trim().toUpperCase() === correctLetter
              );
              if (cEl) cEl.classList.add('correct');
            }
            const res = document.createElement('div');
            res.className = `choice-result ${isCorrect ? 'result-correct' : 'result-incorrect'}`;
            res.innerHTML = isCorrect
              ? `<span class="result-icon">✓</span> Correct!`
              : correctLetter
                ? `<span class="result-icon">✗</span> Incorrect — correct answer is <strong>${correctLetter}</strong>`
                : `<span class="result-icon">•</span> Selected`;
            container.appendChild(res);
            // Auto-reveal solution
            if (qContainer) {
              const sol = qContainer.querySelector('.question-solution')
                       || (qContainer.id ? document.getElementById(qContainer.id) : null);
              const solById = container.closest('[data-qid]')
                ? document.getElementById('question-solution-' + container.closest('[data-qid]').dataset.qid)
                : null;
              const target = sol || solById;
              if (target) setTimeout(() => { target.style.display = 'block'; target.classList.add('sol-reveal'); }, 350);
            }
          });
        });
      };

      // ── Helper: build choice div ──────────────────────
      const makeChoice = (letter, htmlContent) => {
        const div = document.createElement('div');
        div.className = 'answer-choice';
        div.setAttribute('data-letter', letter);
        div.innerHTML = `<span class="choice-letter">${letter}</span><span class="choice-text">${htmlContent}</span>`;
        return div;
      };

      // ── Helper: strip leading letter prefix from innerHTML ─
      // Handles "A)&nbsp;&nbsp;" and "A. " and "A)  " patterns
      const stripLetterPrefix = (html) =>
        html.replace(/^[A-Da-d][).]\s*(&nbsp;| |\s)*/i, '').trim();

      // ── 1) Pre-existing .answer-choices containers ────
      el.querySelectorAll('.answer-choices').forEach(container => {
        const choices = container.querySelectorAll('.answer-choice');
        const qContainer = container.closest('.question, .question-body, .quiz-body, article, section');
        attachChoiceHandlers(container, choices, findCorrect(qContainer), qContainer);
      });

      // ── 2) Single <p> with A./A) + <br> separators ───
      const processedParas = new WeakSet();
      Array.from(el.querySelectorAll('p')).forEach(p => {
        if (processedParas.has(p)) return;
        const rawText = p.textContent.replace(/ /g, ' ').trim();
        if (!/^[Aa][).]/i.test(rawText)) return;

        const lines = p.innerHTML.split(/<br\s*\/?>/gi);
        const choices = [];
        lines.forEach(line => {
          const clean = line.replace(/<[^>]+>/g, '').replace(/ /g, ' ').trim();
          const m = clean.match(/^([A-Da-d])[).]\s*(.+)/);
          if (m) choices.push({ letter: m[1].toUpperCase(), html: stripLetterPrefix(line) });
        });
        if (choices.length < 2) return;

        const qContainer = p.closest('.question, .question-body, .quiz-body, article, section');
        const wrap = document.createElement('div');
        wrap.className = 'answer-choices';
        choices.forEach(({ letter, html }) => wrap.appendChild(makeChoice(letter, html)));
        p.replaceWith(wrap);
        processedParas.add(p);
        attachChoiceHandlers(wrap, wrap.querySelectorAll('.answer-choice'), findCorrect(qContainer), qContainer);
      });

      // ── 3) Consecutive separate <p> tags: A) … / B) … / C) … / D) … ─
      const allParas = Array.from(el.querySelectorAll('p:not([data-choice-done])'));
      let idx = 0;
      while (idx < allParas.length) {
        const p = allParas[idx];
        const rawText = p.textContent.replace(/ /g, ' ').trim();
        if (!/^[Aa][).]/i.test(rawText)) { idx++; continue; }

        // Gather consecutive B/C/D paragraphs
        const group = [{ letter: 'A', p, html: stripLetterPrefix(p.innerHTML) }];
        const nextLetters = [['B','b'], ['C','c'], ['D','d']];
        let j = idx + 1;
        for (const [upper, lower] of nextLetters) {
          if (j >= allParas.length) break;
          const np = allParas[j];
          if (processedParas.has(np)) { j++; continue; }
          const nt = np.textContent.replace(/ /g, ' ').trim();
          if (new RegExp(`^[${upper}${lower}][).]`).test(nt)) {
            group.push({ letter: upper, p: np, html: stripLetterPrefix(np.innerHTML) });
            j++;
          } else break;
        }

        if (group.length >= 2) {
          const qContainer = group[0].p.closest('.question, .question-body, .quiz-body, article, section');
          const wrap = document.createElement('div');
          wrap.className = 'answer-choices';
          group.forEach(({ letter, html }) => wrap.appendChild(makeChoice(letter, html)));
          // Replace first <p> with the wrap, remove others
          group[0].p.replaceWith(wrap);
          group.slice(1).forEach(({ p: rp }) => rp.remove());
          group.forEach(({ p: gp }) => processedParas.add(gp));
          attachChoiceHandlers(wrap, wrap.querySelectorAll('.answer-choice'), findCorrect(qContainer), qContainer);
          idx = j;
        } else {
          idx++;
        }
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [content]);

  // Setup solution toggle functionality
  useEffect(() => {
    const openSolution = (sol, btn) => {
      sol.style.display = 'block';
      requestAnimationFrame(() => sol.classList.add('sol-reveal'));
      btn.textContent = '−';
      const toggle = btn.closest('.solution-toggle');
      if (toggle) toggle.setAttribute('data-open', '1');
    };
    const closeSolution = (sol, btn) => {
      sol.style.display = 'none';
      sol.classList.remove('sol-reveal');
      btn.textContent = '+';
      const toggle = btn.closest('.solution-toggle');
      if (toggle) toggle.removeAttribute('data-open');
    };
    window.solutionToggle = (btn, qNum) => {
      const sol = document.getElementById(`question-solution-${qNum}`);
      if (!sol) return;
      const isHidden = sol.style.display === 'none' || sol.style.display === '';
      isHidden ? openSolution(sol, btn) : closeSolution(sol, btn);
    };
    window.toggleSolution = (btn) => {
      const q = btn.closest('.question, .solution-container');
      if (!q) return;
      const sol = q.querySelector('.question-solution');
      if (!sol) return;
      const isHidden = sol.style.display === 'none' || sol.style.display === '';
      isHidden ? openSolution(sol, btn) : closeSolution(sol, btn);
    };

    // 정답 확인 함수: 이전 형제 요소(문제 테이블)의 모든 입력값을 확인하여 스타일을 변경합니다.
    window.checkAnswers = (btn) => {
      const matchingTable = btn.previousElementSibling;
      if (matchingTable) {
        const inputs = matchingTable.querySelectorAll('.matching-input');
        
        inputs.forEach(input => {
          const correctAnswer = input.getAttribute('data-correct');
          const userAnswer = input.value.toUpperCase();
          
          if (userAnswer === correctAnswer) {
            input.style.backgroundColor = '#34D399';
            input.style.color = '#064E3B';
            input.style.borderColor = '#34D399';
          } else {
            input.style.backgroundColor = '#F87171';
            input.style.color = '#7F1D1D';
            input.style.borderColor = '#F87171';
          }
        });
      }
    };
  }, []);

  // Load KaTeX scripts and styles
  useEffect(() => {
    // Add KaTeX CSS if not already present
    if (!document.querySelector('link[href*="katex.min.css"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.css';
      link.integrity = 'sha384-7lU0muIg/i1plk7MgygDUp3/bNRA65orrBub4/OSWHECgwEsY83HaS1x3bljA/XV';
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    }

    const loadKatex = () => {
      if (!window.katex) {
        return new Promise((resolve) => {
          const script = document.createElement('script');
          script.src = 'https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.js';
          script.integrity = 'sha384-RdymN7NRJ+XoyeRY4185zXaxq9QWOOx3O7beyyrRK4KQZrPlCDQQpCu95FoCGPAE';
          script.crossOrigin = 'anonymous';
          script.onload = resolve;
          document.head.appendChild(script);
        });
      }
      return Promise.resolve();
    };

    const loadAutoRender = () => {
      if (!window.renderMathInElement) {
        return new Promise((resolve) => {
          const script = document.createElement('script');
          script.src = 'https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/contrib/auto-render.min.js';
          script.integrity = 'sha384-+VBxd3r6XgURycqtZ117nYw44OOcIax56Z4dCRWbxyPt0Koah1uHoK0o4+/RRE05';
          script.crossOrigin = 'anonymous';
          script.defer = true;
          script.onload = resolve;
          document.head.appendChild(script);
        });
      }
      return Promise.resolve();
    };

    const renderMath = () => {
      if (contentRef.current && window.renderMathInElement) {
        try {
          window.renderMathInElement(contentRef.current, {
            delimiters: [
              { left: "$$", right: "$$", display: false },
              { left: "$", right: "$", display: false },
              { left: "\\(", right: "\\)", display: false },
              { left: "\\[", right: "\\]", display: true },
              { left: "\\begin{array}", right: "\\end{array}", display: true },
            ],
            throwOnError: false,
          });
        } catch (err) {
          console.error('Error rendering math:', err);
        }
      }
    };

    if (content && contentRef.current) {
      Promise.resolve()
        .then(loadKatex)
        .then(loadAutoRender)
        .then(() => {
          setTimeout(renderMath, 100);
        })
        .catch(err => {
          console.error('Error loading KaTeX:', err);
        });
    }
  }, [content]);

  // Handle "Ask AI" button clicks within content
  useEffect(() => {
    if (contentRef.current && setChatInput) {
      const handleClick = (e) => {
        const target = e.target;
        if (target.classList.contains('ask-ai-button')) {
          const question = target.getAttribute('data-question');
          if (question) {
            setChatInput(question);
            // Scroll to chat input
            const chatInput = document.querySelector('.chat-input');
            if (chatInput) {
              chatInput.scrollIntoView({ behavior: 'smooth' });
              chatInput.focus();
            }
          }
        }
      };

      contentRef.current.addEventListener('click', handleClick);
      return () => {
        if (contentRef.current) {
          contentRef.current.removeEventListener('click', handleClick);
        }
      };
    }
  }, [contentRef, setChatInput, content]);

  // Close modal handler
  const handleCloseModal = () => {
    setModalImage(prev => ({ ...prev, isOpen: false }));
  };

  if (error) {
    return <div className="bg-red-50 border border-red-200 p-4 rounded-xl text-red-600 text-sm">Error loading content: {error}</div>;
  }

  if (!content) {
    return <div className="flex justify-center items-center p-8">
      <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-blue-500"></div>
    </div>;
  }

  const _scopedStylesUnused = `/* ═══════════════════════════════════════════════════════
   Course Content Styles  v3
   Scope: .content-body  (React wrapper in HTMLContent.jsx)
   Override: .content-body .content.prose  (HTML file inner wrapper)
             defeats tufte.css dark rules (specificity 0,2,1)
             with specificity 0,3,1
   ═══════════════════════════════════════════════════════ */

/* ── 1. BASE ─────────────────────────────────────────── */
.content-body {
  color: #1e293b !important;
  background: transparent !important;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
  font-size: 1rem;
  line-height: 1.75;
  -webkit-font-smoothing: antialiased;
}
.content-body * { box-sizing: border-box; }
.content-body article, .content-body section,
.content-body .content, .content-body .content.prose { background: transparent !important; }

/* ── 2. TYPOGRAPHY ───────────────────────────────────── */
.content-body h1 {
  font-size: 1.9rem !important;
  font-weight: 800 !important;
  color: #0f172a !important;
  line-height: 1.15 !important;
  letter-spacing: -0.025em !important;
  margin: 0 0 0.5rem !important;
  text-align: left !important;
  background: transparent !important;
}
.content-body h1 + p,
.content-body h1 + div { margin-top: 1.5rem; }

.content-body h2 {
  font-size: 1.2rem !important;
  font-weight: 700 !important;
  color: #1e3a8a !important;
  margin: 2.5rem 0 0.75rem !important;
  text-align: left !important;
  background: transparent !important;
  padding-bottom: 0.4rem !important;
  border-bottom: 2px solid #dbeafe !important;
}
.content-body h3 {
  font-size: 1rem !important;
  font-weight: 700 !important;
  color: #1e40af !important;
  margin: 1.5rem 0 0.5rem !important;
  text-align: left !important;
  background: transparent !important;
}
.content-body h4 {
  font-size: 0.9rem !important;
  font-weight: 600 !important;
  color: #334155 !important;
  margin: 1rem 0 0.3rem !important;
  background: transparent !important;
}
.content-body p {
  color: #334155 !important;
  margin: 0 0 1rem !important;
  line-height: 1.8 !important;
  font-size: 0.95rem !important;
  text-align: left !important;
  background: transparent !important;
}
.content-body strong, .content-body b { color: #0f172a !important; font-weight: 700 !important; }
.content-body em { color: #1d4ed8 !important; font-style: italic !important; }
.content-body a { color: #2563eb !important; text-decoration: underline !important; text-underline-offset: 2px; }
.content-body a:hover { color: #1d4ed8 !important; }
.content-body ul, .content-body ol {
  color: #334155 !important;
  padding-left: 1.6rem !important;
  margin: 0.5rem 0 1rem !important;
}
.content-body li {
  color: #334155 !important;
  margin-bottom: 0.35rem !important;
  line-height: 1.7 !important;
}
.content-body code {
  font-family: 'Roboto Mono', monospace !important;
  font-size: 0.85em !important;
  background: #eff6ff !important;
  color: #1d4ed8 !important;
  padding: 0.15em 0.45em !important;
  border-radius: 5px !important;
  border: 1px solid #dbeafe !important;
}
.content-body pre {
  background: #f1f5f9 !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 10px !important;
  padding: 1rem 1.25rem !important;
  overflow-x: auto !important;
  margin: 1rem 0 !important;
}
.content-body pre code { background: none !important; border: none !important; color: #1e293b !important; }

/* ── 3. LAYOUT ───────────────────────────────────────── */
.content-body article { max-width: 100% !important; background: transparent !important; }
.content-body section { margin: 1.5rem 0 !important; background: transparent !important; }
.content-body .fullwidth { width: 100% !important; clear: both !important; overflow: hidden !important; }
.content-body .minipage { margin: 0 auto !important; }

/* ── 4. TABLES ───────────────────────────────────────── */
.content-body table {
  width: 100% !important;
  border-collapse: collapse !important;
  margin: 1.25rem 0 !important;
  border-radius: 10px !important;
  overflow: hidden !important;
  border: 1px solid #e2e8f0 !important;
  background: #fff !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important;
}
.content-body th {
  background: linear-gradient(90deg,#2563eb,#3b82f6) !important;
  color: #fff !important;
  font-weight: 600 !important;
  font-size: 0.83rem !important;
  padding: 0.7rem 1rem !important;
  text-align: left !important;
  letter-spacing: 0.04em !important;
}
.content-body td {
  padding: 0.65rem 1rem !important;
  color: #374151 !important;
  border-bottom: 1px solid #f1f5f9 !important;
  font-size: 0.93rem !important;
  text-align: left !important;
  background: #fff !important;
}
.content-body tr:last-child td { border-bottom: none !important; }
.content-body tr:nth-child(even) td { background: #f8fafc !important; }
.content-body tr:hover td { background: #eff6ff !important; }

/* ── 5. SECTION HEADING ──────────────────────────────── */
.content-body .section-heading {
  font-size: 1.15rem !important;
  font-weight: 700 !important;
  color: #1e3a8a !important;
  border-bottom: 2px solid #dbeafe !important;
  padding-bottom: 0.5rem !important;
  margin: 2rem 0 1rem !important;
  text-align: left !important;
  background: transparent !important;
}

/* ── 6. STRATEGY CARDS ───────────────────────────────── */
.content-body .strategy-container {
  display: flex !important;
  flex-direction: column !important;
  gap: 0.75rem !important;
  margin: 1.5rem 0 !important;
}
.content-body .strategy-card {
  display: flex !important;
  gap: 0 !important;
  align-items: stretch !important;
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 14px !important;
  overflow: hidden !important;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05) !important;
  transition: box-shadow .2s, transform .2s;
}
.content-body .strategy-card:hover {
  box-shadow: 0 6px 20px rgba(37,99,235,0.1) !important;
  transform: translateY(-1px) !important;
}
.content-body .strategy-number {
  flex-shrink: 0 !important;
  width: 52px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background: linear-gradient(160deg, #2563eb, #7c3aed) !important;
  color: #fff !important;
  font-weight: 800 !important;
  font-size: 1.15rem !important;
  letter-spacing: -0.02em !important;
}
.content-body .strategy-content {
  flex: 1 !important;
  min-width: 0 !important;
  padding: 1rem 1.25rem !important;
  border-left: none !important;
}
.content-body .strategy-content h3 {
  margin: 0 0 0.35rem !important;
  color: #1e3a8a !important;
  font-size: 0.95rem !important;
  font-weight: 700 !important;
  background: transparent !important;
}
.content-body .strategy-content p {
  color: #475569 !important;
  margin: 0 !important;
  font-size: 0.88rem !important;
  line-height: 1.65 !important;
  background: transparent !important;
}

/* ── 7. TCOLORBOX ────────────────────────────────────── */
.content-body .tcolorbox {
  background: #ffffff !important;
  border: 1px solid #dbeafe !important;
  border-radius: 16px !important;
  overflow: hidden !important;
  margin: 1.5rem auto !important;
  max-width: 520px !important;
  width: 100% !important;
  box-shadow: 0 4px 16px rgba(37,99,235,0.08) !important;
}
.content-body .tcolorbox-title {
  background: linear-gradient(90deg, #2563eb, #6d28d9) !important;
  color: #fff !important;
  font-weight: 700 !important;
  font-size: 0.8rem !important;
  padding: 0.65rem 1.25rem !important;
  letter-spacing: 0.1em !important;
  text-transform: uppercase !important;
  display: flex !important;
  align-items: center !important;
  gap: 0.4rem !important;
}
.content-body .tcolorbox-body {
  padding: 1.1rem 1.25rem !important;
  background: #f8faff !important;
  color: #1e293b !important;
}
.content-body .tcolorbox-body p {
  color: #334155 !important;
  font-size: 0.93rem !important;
  margin: 0.3rem 0 !important;
  background: transparent !important;
}
.content-body .tcolorbox-body ol,
.content-body .tcolorbox-body ul {
  color: #334155 !important;
  text-align: left !important;
  margin: 0.5rem 0 !important;
}
.content-body .tcolorbox-body li {
  color: #334155 !important;
  font-size: 0.92rem !important;
  padding: 0.15rem 0 !important;
}

/* ── 8. EXAMPLE BOXES ────────────────────────────────── */
.content-body .example,
.content-body .examplesplit,
.content-body .examplefull {
  position: relative !important;
  background: #f0f9ff !important;
  border-left: 4px solid #3b82f6 !important;
  border-top: none !important; border-right: none !important; border-bottom: none !important;
  border-radius: 0 12px 12px 0 !important;
  padding: 1.25rem 1.4rem 1.25rem 1.5rem !important;
  margin: 1.5rem 0 !important;
  box-shadow: 0 1px 4px rgba(59,130,246,0.08) !important;
}
.content-body .example p,
.content-body .examplesplit p,
.content-body .examplefull p {
  color: #1e3a8a !important;
  text-align: left !important;
  background: transparent !important;
}

/* ── 9. PROBLEM SETS ─────────────────────────────────── */
.content-body .problem-set {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 14px !important;
  overflow: hidden !important;
  margin: 1.5rem 0 !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05) !important;
}
.content-body .problem-set h3 {
  background: linear-gradient(90deg,#1e40af,#2563eb) !important;
  color: #fff !important;
  padding: 0.75rem 1.1rem !important;
  margin: 0 !important;
  font-size: 0.88rem !important; font-weight: 700 !important;
  letter-spacing: 0.04em !important;
  text-align: left !important;
}
.content-body .problem-table { width: 100% !important; border-collapse: collapse !important; background: #ffffff !important; }
.content-body .problem-table tr,
.content-body .problem-table tr:nth-child(even),
.content-body .problem-table tr:nth-child(odd) { background: #ffffff !important; }
.content-body .problem-table td,
.content-body .problem-table td:nth-child(even),
.content-body .problem-table td:nth-child(odd) {
  padding: 0.9rem 1.1rem !important;
  color: #374151 !important;
  border-bottom: 1px solid #f1f5f9 !important;
  font-size: 0.93rem !important;
  background: #ffffff !important;
}
.content-body .problem-table tr:last-child td { border-bottom: none !important; }
.content-body .problem-table tr:hover td { background: #f8fafc !important; }

/* ── 10. QUESTION CARDS ──────────────────────────────── */
.content-body .question {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 14px !important;
  overflow: hidden !important;
  margin: 1.5rem 0 !important;
  box-shadow: 0 2px 10px rgba(0,0,0,0.06) !important;
}
.content-body .question-label {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  padding: 0.6rem 1rem !important;
  background: #f8fafc !important;
  border-bottom: 1px solid #e2e8f0 !important;
  gap: 0.75rem !important;
}
.content-body .question-label-box {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  min-width: 28px !important;
  height: 28px !important;
  padding: 0 0.5rem !important;
  background: linear-gradient(135deg, #2563eb, #7c3aed) !important;
  color: #fff !important;
  font-weight: 700 !important;
  font-size: 0.8rem !important;
  border-radius: 8px !important;
  letter-spacing: 0.02em !important;
  text-transform: none !important;
}
.content-body .question-body {
  padding: 1.25rem !important;
  color: #1e293b !important;
  background: #ffffff !important;
}
.content-body .question-body p { color: #374151 !important; background: transparent !important; }
.content-body .question-prompt p {
  font-weight: 600 !important; color: #1e3a8a !important;
  border-left: 3px solid #3b82f6 !important; padding-left: 0.75rem !important;
  margin: 0.75rem 0 !important; background: transparent !important;
}

/* ── 11. SOLUTION TOGGLE ─────────────────────────────── */
.content-body .solution-toggle {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  padding: 0.65rem 1rem !important;
  background: #f8fafc !important;
  border-top: 1px solid #e2e8f0 !important;
  cursor: pointer !important;
  transition: background .15s;
}
.content-body .solution-toggle:hover {
  background: #f1f5f9 !important;
}
.content-body .solution-label {
  font-size: 0.82rem !important;
  font-weight: 600 !important;
  color: #2563eb !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
}
.content-body .solution-label::before { content: none !important; }
.content-body .question-solution-button {
  display: inline-flex !important;
  align-items: center !important;
  gap: 0.3rem !important;
  padding: 0.3rem 0.7rem !important;
  border-radius: 99px !important;
  background: #eff6ff !important;
  color: #2563eb !important;
  border: 1px solid #bfdbfe !important;
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  cursor: pointer !important;
  transition: background .15s, border-color .15s;
  float: none !important;
  box-shadow: none !important;
  white-space: nowrap !important;
  margin-left: auto !important;
}
.content-body .question-solution-button::before {
  content: 'Solution' !important;
  font-size: 0.75rem !important;
}
.content-body .question-solution-button:hover {
  background: #dbeafe !important;
  border-color: #93c5fd !important;
  transform: none !important;
  box-shadow: none !important;
}

/* ── 11b. SOLUTION CONTAINER (alt toggle pattern) ───── */
.content-body .solution-container {
  margin-top: 1.25rem !important;
  border-radius: 10px !important;
  overflow: hidden !important;
  background: transparent !important;
  border: 1px solid #e2e8f0 !important;
}
.content-body .solution-toggle {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  padding: 0.65rem 1rem !important;
  background: #f8fafc !important;
  border-top: none !important;
  cursor: pointer !important;
  user-select: none !important;
  transition: background .15s;
}
.content-body .solution-toggle:hover {
  background: #f1f5f9 !important;
}
.content-body .solution-label {
  font-size: 0.82rem !important;
  font-weight: 600 !important;
  color: #2563eb !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
}
.content-body .solution-label::before { content: none !important; }
.content-body .solution-button {
  width: 22px !important;
  height: 22px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background: #e2e8f0 !important;
  color: #475569 !important;
  font-weight: 700 !important;
  font-size: 0.9rem !important;
  border-radius: 50% !important;
  cursor: pointer !important;
  transition: background .15s;
  border-radius: 50% !important;
}
.content-body .solution-button:hover { background: #cbd5e1 !important; }
.content-body .solution-content {
  display: none;
  padding: 1rem 1.25rem !important;
  background: #f8faff !important;
  border-top: 1px solid #e2e8f0 !important;
  color: #334155 !important;
}
.content-body .solution-content h4 {
  color: #1e3a8a !important;
  font-size: 0.88rem !important;
  font-weight: 700 !important;
  margin: 0 0 0.5rem !important;
  background: transparent !important;
}
.content-body .solution-content ul,
.content-body .solution-content ol { color: #334155 !important; }
.content-body .solution-content li {
  color: #334155 !important;
  font-size: 0.9rem !important;
  line-height: 1.65 !important;
}
.content-body .solution-content p { color: #334155 !important; background: transparent !important; }

/* ── 12. SOLUTION PANEL ──────────────────────────────── */
.content-body .question-solution {
  margin: 0 !important;
  padding: 0.85rem 1.25rem 1.1rem !important;
  border-top: 1px solid #e0e7ff !important;
  background: #f5f7ff !important;
  overflow: hidden !important;
  color: #1e293b !important;
}
.content-body .question-solution::before {
  content: '📖  Solution' !important;
  display: block !important;
  padding: 0 0 0.6rem !important;
  font-size: 0.72rem !important; font-weight: 800 !important;
  letter-spacing: 0.1em !important; text-transform: uppercase !important;
  color: #6d28d9 !important;
  border-bottom: 1px solid #e0e7ff !important;
  margin-bottom: 0.7rem !important;
}
.content-body .question-solution p { color: #1e293b !important; font-size: 0.91rem !important; line-height: 1.75 !important; background: transparent !important; margin: 0.35rem 0 !important; }
.content-body .question-solution h4 { color: #4338ca !important; font-size: 0.86rem !important; margin: 0.5rem 0 0.25rem !important; background: transparent !important; }
.content-body .question-solution b,
.content-body .question-solution strong { color: #1e3a8a !important; }
.content-body .question-solution ul,
.content-body .question-solution ol { color: #374151 !important; padding-left: 1.25rem !important; margin: 0.4rem 0 !important; }
.content-body .question-solution li { font-size: 0.88rem !important; line-height: 1.65 !important; color: #374151 !important; margin-bottom: 0.25rem !important; }
.content-body .question-solution .katex-display { margin: 0.5rem 0 !important; }
.content-body .question-solution.sol-reveal { animation: fadeSlideIn .35s ease forwards !important; }

/* ── 13. CORRECT/INCORRECT BANNERS ───────────────────── */
.content-body .correct-answer {
  display: flex !important; align-items: center !important; gap: 0.6rem !important;
  background: #ecfdf5 !important; border: 1px solid #a7f3d0 !important;
  border-radius: 8px !important; padding: 0.7rem 1rem !important;
  margin-bottom: 0.75rem !important; color: #065f46 !important; font-weight: 600 !important;
}
.content-body .correct-badge {
  width: 22px !important; height: 22px !important;
  background: #10b981 !important; color: #fff !important;
  border-radius: 50% !important; display: flex !important;
  align-items: center !important; justify-content: center !important;
  font-size: 0.8rem !important; font-weight: 800 !important; flex-shrink: 0 !important;
}
.content-body .explanation h4 { color: #1e293b !important; font-size: 0.88rem !important; margin-bottom: 0.4rem !important; background: transparent !important; }
.content-body .explanation li { color: #374151 !important; font-size: 0.88rem !important; }

/* ── 14. QUIZ CARD ───────────────────────────────────── */
.content-body .quiz-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 14px !important;
  overflow: hidden !important;
  margin: 1.5rem 0 !important;
  box-shadow: 0 2px 10px rgba(0,0,0,0.06) !important;
}
.content-body .quiz-header {
  background: linear-gradient(90deg,#1e40af,#2563eb) !important;
  padding: 0.7rem 1.1rem !important;
}
.content-body .quiz-number { color: #fff !important; font-weight: 700 !important; font-size: 0.85rem !important; }
.content-body .quiz-body { padding: 1.25rem !important; background: #ffffff !important; }

/* ── 15. COLUMNS ─────────────────────────────────────── */
.content-body .columns { display: flex !important; gap: 1rem !important; margin: 1.25rem 0 !important; flex-wrap: wrap !important; }
.content-body .columns .column {
  flex: 1 !important; min-width: 200px !important;
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important; padding: 1.1rem !important;
}
.content-body .columns .column h4 {
  color: #1e40af !important; font-size: 0.88rem !important; font-weight: 700 !important;
  margin: 0 0 0.5rem !important; padding-bottom: 0.4rem !important;
  border-bottom: 1px solid #dbeafe !important; background: transparent !important;
}
.content-body .columns .column p { color: #475569 !important; font-size: 0.88rem !important; margin: 0.3rem 0 !important; background: transparent !important; }

/* ── 16. MARGIN NOTES ────────────────────────────────── */
.content-body .marginnote,
.content-body .sidenote {
  float: right !important; clear: right !important;
  margin-right: -26% !important; width: 22% !important;
  background: #f0f9ff !important;
  border-left: 3px solid #3b82f6 !important;
  border-radius: 0 10px 10px 0 !important;
  padding: 0.7rem 0.9rem !important;
  color: #1e40af !important;
  font-size: 0.81rem !important; line-height: 1.55 !important;
  margin-top: 0.25rem !important; margin-bottom: 1rem !important;
  box-shadow: 0 1px 4px rgba(37,99,235,0.06) !important;
  top: auto !important; left: auto !important; transform: none !important;
}
.content-body .marginnote p,
.content-body .sidenote p { color: #1e40af !important; font-size: 0.81rem !important; margin: 0.25rem 0 !important; background: transparent !important; }
.content-body .marginnote::before,
.content-body .sidenote::before { display: none !important; }

/* ── 17. MISC ────────────────────────────────────────── */
.content-body .circle {
  display: inline-flex !important; align-items: center !important; justify-content: center !important;
  width: 28px !important; height: 28px !important;
  border: 2px solid #2563eb !important; border-radius: 50% !important;
  color: #1d4ed8 !important; font-weight: 800 !important;
  background: #eff6ff !important;
  margin: 0 3px !important; font-size: 0.82rem !important; vertical-align: middle !important;
}
.content-body .newthought { font-variant: small-caps !important; color: #1d4ed8 !important; font-weight: 700 !important; }
.content-body img {
  max-width: 100% !important; height: auto !important; border-radius: 10px !important;
  margin: 1rem auto !important; display: block !important;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08) !important;
}
.content-body video {
  border-radius: 12px !important;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1) !important;
  user-select: none !important;
  -webkit-user-select: none !important;
  pointer-events: auto !important;
}
.content-body video::-webkit-media-controls-download-button { display: none !important; }
.content-body video::-webkit-media-controls-fullscreen-button { display: none !important; }
.content-body .matching-input {
  background: #f8fafc !important; border: 1.5px solid #cbd5e1 !important;
  border-radius: 8px !important; padding: 0.35rem 0.6rem !important;
  color: #1e293b !important; font-size: 0.9rem !important;
  transition: border-color .15s, box-shadow .15s;
}
.content-body .matching-input:focus {
  outline: none !important; border-color: #2563eb !important;
  box-shadow: 0 0 0 3px rgba(37,99,235,0.12) !important;
}

/* ── 18. ANSWER CHOICES ──────────────────────────────── */
.content-body .answer-choices,
.content-body .choices-container {
  display: flex !important;
  flex-direction: column !important;
  gap: 0.5rem !important;
  margin: 1.25rem 0 1.25rem !important;
}

.content-body .answer-choices .answer-choice,
.content-body .choices-container .answer-choice,
.content-body .answer-choice {
  display: flex !important;
  align-items: center !important;
  gap: 1rem !important;
  padding: 1rem 1.25rem !important;
  background: #f8fafc !important;
  border: 1.5px solid #e2e8f0 !important;
  border-radius: 12px !important;
  cursor: pointer !important;
  transition: background .15s, border-color .15s, box-shadow .15s;
  width: 100% !important;
  text-align: left !important;
}
.content-body .answer-choices .answer-choice:hover:not(.correct):not(.incorrect),
.content-body .choices-container .answer-choice:hover:not(.correct):not(.incorrect),
.content-body .answer-choice:hover:not(.correct):not(.incorrect) {
  background: #eff6ff !important;
  border-color: #93c5fd !important;
  box-shadow: 0 2px 8px rgba(37,99,235,0.08) !important;
}
.content-body .answer-choices.answered .answer-choice,
.content-body .choices-container.answered .answer-choice {
  cursor: default !important;
}

.content-body .choice-letter {
  flex-shrink: 0 !important;
  width: 44px !important;
  height: 44px !important;
  border-radius: 50% !important;
  background: #fff !important;
  border: 2px solid #cbd5e1 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-weight: 700 !important;
  font-size: 1rem !important;
  color: #475569 !important;
  transition: all .15s;
}
.content-body .choice-text {
  flex: 1 !important;
  font-size: 1.02rem !important;
  color: #1e293b !important;
  line-height: 1.6 !important;
  background: transparent !important;
}

/* Correct */
.content-body .answer-choice.correct {
  background: #f0fdf4 !important;
  border-color: #22c55e !important;
}
.content-body .answer-choice.correct .choice-letter {
  background: #22c55e !important;
  border-color: #22c55e !important;
  color: #fff !important;
}
.content-body .answer-choice.correct .choice-text {
  color: #166534 !important;
  font-weight: 600 !important;
}

/* Incorrect */
.content-body .answer-choice.incorrect {
  background: #fff1f2 !important;
  border-color: #f43f5e !important;
  animation: shake .35s ease;
}
.content-body .answer-choice.incorrect .choice-letter {
  background: #f43f5e !important;
  border-color: #f43f5e !important;
  color: #fff !important;
}
.content-body .answer-choice.incorrect .choice-text { color: #9f1239 !important; }

/* Result banner */
.content-body .choice-result {
  display: flex !important;
  align-items: center !important;
  gap: 0.5rem !important;
  padding: 0.65rem 1rem !important;
  border-radius: 8px !important;
  font-size: 0.87rem !important;
  font-weight: 600 !important;
  margin-top: 0.4rem !important;
  animation: fadeSlideIn .25s ease;
}
.content-body .result-correct {
  background: #f0fdf4 !important;
  border: 1px solid #bbf7d0 !important;
  color: #15803d !important;
}
.content-body .result-incorrect {
  background: #fff1f2 !important;
  border: 1px solid #fecdd3 !important;
  color: #be123c !important;
}
.content-body .result-icon { font-size: 0.95rem !important; }

/* ── 19. ANIMATIONS ──────────────────────────────────── */
@keyframes fadeSlideIn { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }
@keyframes shake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-6px)} 40%{transform:translateX(6px)} 60%{transform:translateX(-4px)} 80%{transform:translateX(4px)} }

/* ══════════════════════════════════════════════════════
   20. OVERRIDE tufte.css  (.content.prose → 0,2,1 spec)
   We use .content-body .content.prose  → 0,3,1  — wins
   ══════════════════════════════════════════════════════ */
.content-body .content.prose { color: #1e293b !important; background: transparent !important; }
.content-body .content.prose h1,
.content-body .content.prose h2,
.content-body .content.prose h3,
.content-body .content.prose h4,
.content-body .content.prose h5,
.content-body .content.prose h6 { color: #0f172a !important; text-align: left !important; background: transparent !important; }
.content-body .content.prose h1 { color: #0f172a !important; font-size: 1.9rem !important; }
.content-body .content.prose h2 { color: #1e3a8a !important; font-size: 1.2rem !important; }
.content-body .content.prose h3 { color: #1e40af !important; font-size: 1rem !important; }
.content-body .content.prose h4 { color: #334155 !important; font-size: 0.9rem !important; }
.content-body .content.prose p  { color: #334155 !important; background: transparent !important; }
.content-body .content.prose li { color: #334155 !important; }
.content-body .content.prose a  { color: #2563eb !important; }
.content-body .content.prose strong, .content-body .content.prose b { color: #0f172a !important; }
.content-body .content.prose th { color: #fff !important; background: linear-gradient(90deg,#2563eb,#3b82f6) !important; }
.content-body .content.prose td { color: #374151 !important; background: #ffffff !important; }
.content-body .content.prose tr:nth-child(even) td { background: #f8fafc !important; }
.content-body .content.prose .problem-set { background: #ffffff !important; box-shadow: 0 2px 8px rgba(0,0,0,0.05) !important; }
.content-body .content.prose .problem-table,
.content-body .content.prose .problem-table tr,
.content-body .content.prose .problem-table tr:nth-child(even),
.content-body .content.prose .problem-table tr:nth-child(odd),
.content-body .content.prose .problem-table td,
.content-body .content.prose .problem-table td:nth-child(even),
.content-body .content.prose .problem-table td:nth-child(odd) { background: #ffffff !important; color: #374151 !important; }
.content-body .content.prose .problem-table tr:hover td { background: #f8fafc !important; }
.content-body .content.prose .question { background: #ffffff !important; }
.content-body .content.prose .question-label {
  background: #f8fafc !important;
  border-color: #e2e8f0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  padding: 0.6rem 1rem !important;
}
.content-body .content.prose .question-label-box {
  background: linear-gradient(135deg,#2563eb,#7c3aed) !important;
  color: #fff !important;
  border-radius: 8px !important;
  padding: 0 0.5rem !important;
  min-width: 28px !important;
  height: 28px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-size: 0.8rem !important;
  font-weight: 700 !important;
}
.content-body .content.prose .question-body { color: #1e293b !important; background: transparent !important; }
.content-body .content.prose .question-body p { color: #374151 !important; background: transparent !important; }
.content-body .content.prose .question-solution { background: linear-gradient(135deg,#f0f9ff,#f5f3ff) !important; }
.content-body .content.prose .columns .column { background: #ffffff !important; color: #1e293b !important; border-color: #e2e8f0 !important; }
.content-body .content.prose .columns .column h4 { color: #1e40af !important; background: transparent !important; }
.content-body .content.prose .columns .column p  { color: #475569 !important; background: transparent !important; }
.content-body .content.prose .sidenote,
.content-body .content.prose .marginnote { color: #1e40af !important; background: #f0f9ff !important; top: auto !important; left: auto !important; transform: none !important; }
.content-body .content.prose .sidenote p,
.content-body .content.prose .marginnote p { color: #1e40af !important; background: transparent !important; }
.content-body .content.prose .sidenote::before,
.content-body .content.prose .marginnote::before { display: none !important; }
.content-body .content.prose .strategy-card { background: #ffffff !important; border-color: #e2e8f0 !important; overflow: hidden !important; }
.content-body .content.prose .strategy-number { background: linear-gradient(160deg,#2563eb,#7c3aed) !important; color: #fff !important; width: 52px !important; }
.content-body .content.prose .strategy-content h3 { color: #1e3a8a !important; background: transparent !important; }
.content-body .content.prose .strategy-content p  { color: #475569 !important; background: transparent !important; }
.content-body .content.prose .tcolorbox { background: #ffffff !important; border-color: #dbeafe !important; border-radius: 16px !important; margin: 1.5rem auto !important; max-width: 520px !important; width: 100% !important; }
.content-body .content.prose .tcolorbox-title { background: linear-gradient(90deg,#2563eb,#6d28d9) !important; color: #fff !important; }
.content-body .content.prose .tcolorbox-body { background: #f8faff !important; color: #1e293b !important; }
.content-body .content.prose .tcolorbox-body p { color: #334155 !important; background: transparent !important; }
.content-body .content.prose .example,
.content-body .content.prose .examplesplit,
.content-body .content.prose .examplefull { background: #f0f9ff !important; border-left-color: #3b82f6 !important; border-top: none !important; border-right: none !important; border-bottom: none !important; }
.content-body .content.prose .example p,
.content-body .content.prose .examplesplit p,
.content-body .content.prose .examplefull p { color: #1e3a8a !important; background: transparent !important; }
.content-body .content.prose .solution-container {
  background: transparent !important;
  border: 1px solid #e2e8f0 !important;
}
.content-body .content.prose .solution-toggle {
  background: #f8fafc !important;
  border-top: none !important;
}
.content-body .content.prose .solution-toggle:hover { background: #f1f5f9 !important; }
.content-body .content.prose .solution-label { color: #2563eb !important; font-size: 0.82rem !important; font-weight: 600 !important; }
.content-body .content.prose .solution-button {
  background: #e2e8f0 !important;
  color: #475569 !important;
  border-radius: 50% !important;
  width: 22px !important; height: 22px !important;
  font-size: 0.9rem !important;
}
.content-body .content.prose .solution-content {
  background: #f8faff !important;
  border-top: 1px solid #e2e8f0 !important;
  color: #334155 !important;
}
.content-body .content.prose .solution-content h4 { color: #1e3a8a !important; background: transparent !important; }
.content-body .content.prose .solution-content li { color: #334155 !important; }
.content-body .content.prose .solution-content p { color: #334155 !important; background: transparent !important; }
.content-body .content.prose .explanation h4 { color: #1e3a8a !important; background: transparent !important; }
.content-body .content.prose .explanation li { color: #334155 !important; }
.content-body .content.prose .explanation p  { color: #334155 !important; background: transparent !important; }

/* ── 21. RESPONSIVE ──────────────────────────────────── */
@media (max-width: 768px) {
  .content-body h1 { font-size: 1.5rem !important; }
  .content-body h2 { font-size: 1.05rem !important; }
  .content-body .marginnote, .content-body .sidenote,
  .content-body .content.prose .marginnote, .content-body .content.prose .sidenote {
    float: none !important; margin-right: 0 !important; width: 100% !important;
    margin: 0.75rem 0 !important;
  }
  .content-body .columns { flex-direction: column !important; }
  .content-body .strategy-card { flex-direction: column !important; }
  .content-body .answer-choice:hover:not(.selected) { transform: none !important; }
}
`;

  // Inject CSS synchronously into document.head (idempotent, no hooks needed)
  if (typeof document !== 'undefined') {
    const _cssId = 'course-content-scoped-styles';
    let _styleEl = document.getElementById(_cssId);
    if (!_styleEl) {
      _styleEl = document.createElement('style');
      _styleEl.id = _cssId;
      document.head.appendChild(_styleEl);
    }
    if (_styleEl.textContent !== _scopedStylesUnused) _styleEl.textContent = _scopedStylesUnused;
  }

  return (
    <div className="w-full">
      <div
        ref={contentRef}
        className="content-body"
        dangerouslySetInnerHTML={{ __html: content }}
      />
      <ImageModal
        src={modalImage.src}
        alt={modalImage.alt}
        isOpen={modalImage.isOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default HTMLContent;